import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, Mail, Phone, MessageSquare } from 'lucide-react';
import API_BASE_URL from '../config';
import Carousel from '../components/Carousel';
import HomeButton from '../components/HomeButton';
import '../styles/Prenota.css';

const MAX_BOOKINGS_PER_HOUR = 10;
const SLOT_MINUTES = 60;

const Prenota = () => {
    const [formData, setFormData] = useState({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        booking_date: '',
        booking_time: '',
        number_of_guests: 2,
        table_id: 'default-table',
        special_requests: ''
    });

    const [capacityInfo, setCapacityInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [checkingAvailability, setCheckingAvailability] = useState(false);
    const [lastAvailabilityCheck, setLastAvailabilityCheck] = useState(null);
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [calendarError, setCalendarError] = useState('');
    const [lastCalendarFetch, setLastCalendarFetch] = useState(null);

    // Stati per modale cancellazione
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelCode, setCancelCode] = useState('');
    const [cancelBooking, setCancelBooking] = useState(null);
    const [cancelError, setCancelError] = useState('');
    const [cancelSuccess, setCancelSuccess] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);

    const timeSlots = [
        '12:00', '12:30', '13:00', '13:30', '14:00',
        '20:00', '20:30', '21:00', '21:30', '22:00'
    ];

    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    const computeAvailabilityFromCalendar = () => {
        const { booking_date, booking_time } = formData;

        if (!booking_date || !booking_time) return null;
        if (calendarLoading) return null;
        if (calendarError) return null;

        const slotStart = new Date(`${booking_date}T${booking_time}:00`);
        const slotEnd = new Date(slotStart.getTime() + SLOT_MINUTES * 60000);

        let count = 0;

        calendarEvents.forEach(event => {
            if (!event.start || !event.end) return;
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);
            if (eventStart < slotEnd && eventEnd > slotStart) {
                count += 1;
            }
        });

        const available = count < MAX_BOOKINGS_PER_HOUR;
        return {
            available,
            bookingsCount: count,
            maxCapacity: MAX_BOOKINGS_PER_HOUR,
            remainingSlots: Math.max(0, MAX_BOOKINGS_PER_HOUR - count)
        };
    };

    const refreshAvailabilityMessage = () => {
        const availability = computeAvailabilityFromCalendar();
        if (!availability) {
            setCapacityInfo(null);
            return;
        }

        setCapacityInfo(availability);
        setLastAvailabilityCheck(new Date());

        if (availability.available) {
            setMessage({
                type: 'success',
                text: `✅ Disponibile! ${availability.bookingsCount}/${availability.maxCapacity} prenotazioni per questo orario (${availability.remainingSlots} posti rimasti)`
            });
        } else {
            setMessage({
                type: 'error',
                text: `❌ Siamo al completo. ${availability.bookingsCount}/${availability.maxCapacity} prenotazioni per questo orario.`
            });
        }
    };

    const fetchCalendarForDate = async (dateValue) => {
        // Evita richieste con date non valide che generano errori 503 e messaggi fuorvianti
        if (!dateValue || !/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
            setCalendarError('');
            setCalendarEvents([]);
            return;
        }
        setCalendarLoading(true);
        setCalendarError('');
        setCalendarEvents([]);
        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/calendar?date=${dateValue}&days=1`);
            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || data.error || 'Errore caricamento calendario');
            }

            setCalendarEvents(data.events || []);
            setLastCalendarFetch(new Date());
        } catch (err) {
            console.error('Errore caricamento calendario:', err);
            setCalendarError(err.message);
            setMessage({ type: 'error', text: `❌ ${err.message}` });
        } finally {
            setCalendarLoading(false);
        }
    };

    // Carica eventi calendario quando cambia la data
    useEffect(() => {
        if (formData.booking_date) {
            fetchCalendarForDate(formData.booking_date);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.booking_date]);

    // Ricalcola disponibilità localmente quando cambiano ora/ospiti o gli eventi
    useEffect(() => {
        refreshAvailabilityMessage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.booking_time, formData.number_of_guests, calendarEvents, calendarLoading, calendarError]);

    // Cerca prenotazione da cancellare
    const handleSearchCancel = async () => {
        if (!cancelCode || cancelCode.trim() === '') {
            setCancelError('Inserisci un codice prenotazione');
            return;
        }

        if (!cancelCode.startsWith('PREN-')) {
            setCancelError('Codice non valido. Deve iniziare con PREN-');
            return;
        }

        setCancelLoading(true);
        setCancelError('');

        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/code/${cancelCode}`);
            const result = await response.json();

            if (result.success) {
                setCancelBooking(result.booking);
            } else {
                setCancelError(result.error || 'Prenotazione non trovata');
            }
        } catch (err) {
            setCancelError('Errore durante la ricerca');
        } finally {
            setCancelLoading(false);
        }
    };

    // Cancella prenotazione
    const handleCancelBooking = async () => {
        if (!window.confirm('Sei sicuro di voler cancellare questa prenotazione?')) {
            return;
        }

        setCancelLoading(true);
        setCancelError('');

        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings/cancel/${cancelCode}`, {
                method: 'DELETE'
            });
            const result = await response.json();

            if (result.success) {
                setCancelSuccess(true);
                setCancelBooking(null);
            } else {
                setCancelError('Errore durante la cancellazione');
            }
        } catch (err) {
            setCancelError('Errore durante la cancellazione');
        } finally {
            setCancelLoading(false);
        }
    };

    // Chiudi modale
    const closeCancelModal = () => {
        setShowCancelModal(false);
        setCancelCode('');
        setCancelBooking(null);
        setCancelError('');
        setCancelSuccess(false);
    };

    // Aggiorna disponibilità quando si torna al tab
    useEffect(() => {
        const refreshIfReady = () => {
            if (formData.booking_date) {
                fetchCalendarForDate(formData.booking_date);
            }
        };
        
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') refreshIfReady();
        };
        
        window.addEventListener('focus', refreshIfReady);
        document.addEventListener('visibilitychange', onVisibilityChange);
        
        return () => {
            window.removeEventListener('focus', refreshIfReady);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.booking_date]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        // Verifica disponibilità usando i dati in cache del calendario
        const availability = computeAvailabilityFromCalendar();
        if (!availability) {
            setMessage({
                type: 'error',
                text: '❌ Non abbiamo ancora i dati di disponibilità. Aggiorna il calendario e riprova.'
            });
            setLoading(false);
            return;
        }

        if (!availability.available) {
            setMessage({
                type: 'error',
                text: '❌ Il ristorante è al completo per questo orario. Scegli un altro orario.'
            });
            setLoading(false);
            return;
        }

        try {
            // Valida UUID
            let anonymousUserId = localStorage.getItem('anonymous_user_id');
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
            
            if (!anonymousUserId || !uuidRegex.test(anonymousUserId)) {
                console.log('🔄 UUID non valido, rigenerazione...');
                localStorage.removeItem('anonymous_user_id');
                anonymousUserId = generateUUID();
                localStorage.setItem('anonymous_user_id', anonymousUserId);
            }

            const bookingData = {
                customer_name: formData.customer_name,
                customer_email: formData.customer_email,
                customer_phone: formData.customer_phone,
                booking_date: formData.booking_date,
                booking_time: formData.booking_time,
                number_of_guests: parseInt(formData.number_of_guests),
                table_id: formData.table_id,
                anonymous_user_id: anonymousUserId,
                special_requests: formData.special_requests || null
            };

            // Crea prenotazione tramite backend
            const response = await fetch(`${API_BASE_URL}/api/bookings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(bookingData)
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Errore nella creazione della prenotazione');
            }

            if (result.success) {
                const bookingCode = result.bookingCode || 'N/A';
                setMessage({
                    type: 'success',
                    text: result.calendarSynced 
                        ? `✅ Prenotazione confermata!\n\n📧 Ti abbiamo inviato un'email con tutti i dettagli.\n\n🔑 Codice prenotazione: ${bookingCode}\n\nConserva questo codice per modifiche o cancellazioni.` 
                        : `✅ Prenotazione confermata!\n\n🔑 Codice prenotazione: ${bookingCode}`
                });

                // Reset form
                setFormData({
                    customer_name: '',
                    customer_email: '',
                    customer_phone: '',
                    booking_date: '',
                    booking_time: '',
                    number_of_guests: 2,
                    table_id: 'default-table',
                    special_requests: ''
                });
                setCapacityInfo(null);

                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                // Gestisci il caso in cui success è false ma response.ok è true
                setMessage({
                    type: 'error',
                    text: result.nextAvailableSlot 
                        ? `❌ ${result.error || result.message || 'Errore nella prenotazione'}. Prossimo slot disponibile: ${result.nextAvailableSlot}`
                        : `❌ ${result.error || result.message || 'Errore nella prenotazione'}`
                });
            }

        } catch (error) {
            console.error('Errore prenotazione:', error);
            setMessage({
                type: 'error',
                text: '❌ ' + error.message
            });
        } finally {
            setLoading(false);
        }
    };

    const getMinDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const getMaxDate = () => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        return d.toISOString().split('T')[0];
    };

    const handleDateInputChange = (e) => {
        const iso = e.target.value;
        const min = getMinDate();
        const max = getMaxDate();
        if (!iso) {
            setFormData(prev => ({ ...prev, booking_date: '' }));
            return;
        }
        // clamp within [today, today+7]
        if (iso < min) {
            setFormData(prev => ({ ...prev, booking_date: min }));
            return;
        }
        if (iso > max) {
            setFormData(prev => ({ ...prev, booking_date: max }));
            return;
        }
        setFormData(prev => ({ ...prev, booking_date: iso }));
    };

    return (
        <div className="prenota-hero">
            <HomeButton />
            
            {/* Carousel come sfondo */}
            <div className="prenota-hero__bg">
                <Carousel />
            </div>
            
            {/* Contenuto sopra lo sfondo */}
            <div className="prenota-hero__content">
                <div className="prenota-container">
                    <div className="prenota-header">
                        <h1>Prenota il tuo Tavolo</h1>
                        <p>Compila il modulo per prenotare il tuo tavolo all'Ultima Spiaggia</p>
                    </div>

                    {message.text && (
                        <div className={`message ${message.type}`}>
                            {message.text}
                            {lastAvailabilityCheck && message.type !== 'error' && (
                                <div className="message-meta">
                                    Aggiornato alle {lastAvailabilityCheck.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                </div>
                            )}
                            <div className="message-actions">
                                <button
                                    type="button"
                                    className="refresh-btn"
                                    onClick={() => fetchCalendarForDate(formData.booking_date)}
                                    disabled={calendarLoading || !formData.booking_date}
                                >
                                    {calendarLoading ? 'Carico...' : 'Aggiorna calendario'}
                                </button>
                                <button
                                    type="button"
                                    className="refresh-btn"
                                    onClick={refreshAvailabilityMessage}
                                    disabled={calendarLoading || !formData.booking_date || !formData.booking_time}
                                >
                                    Ricalcola disponibilità
                                </button>
                                {lastCalendarFetch && (
                                    <span className="message-meta">Calendario: {lastCalendarFetch.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                                )}
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="prenota-form">
                        {/* Informazioni Cliente */}
                        <div className="form-section">
                            <h2>Informazioni di Contatto</h2>
                            
                            <div className="form-group">
                                <label>
                                    <Users size={20} />
                                    Nome Completo *
                                </label>
                                <input
                                    type="text"
                                    name="customer_name"
                                    value={formData.customer_name}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Mario Rossi"
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>
                                        <Mail size={20} />
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        name="customer_email"
                                        value={formData.customer_email}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="mario.rossi@email.com"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>
                                        <Phone size={20} />
                                        Telefono *
                                    </label>
                                    <input
                                        type="tel"
                                        name="customer_phone"
                                        value={formData.customer_phone}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="+39 123 456 7890"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Dettagli Prenotazione */}
                        <div className="form-section">
                            <h2>Dettagli Prenotazione</h2>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>
                                        <Calendar size={20} />
                                        Data *
                                    </label>
                                    <input
                                        type="date"
                                        name="booking_date"
                                        value={formData.booking_date}
                                        onChange={handleDateInputChange}
                                        min={getMinDate()}
                                        max={getMaxDate()}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>
                                        <Clock size={20} />
                                        Orario *
                                    </label>
                                    <select
                                        name="booking_time"
                                        value={formData.booking_time}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Seleziona orario</option>
                                        {timeSlots.map(time => (
                                            <option key={time} value={time}>{time}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>
                                        <Users size={20} />
                                        Numero Ospiti (max 5) *
                                    </label>
                                    <input
                                        type="number"
                                        name="number_of_guests"
                                        value={formData.number_of_guests}
                                        onChange={handleInputChange}
                                        min="1"
                                        max="5"
                                        required
                                    />
                                    <small style={{ color: '#666', fontSize: '14px', marginTop: '5px', display: 'block' }}>
                                        Per gruppi oltre 5 persone, contattaci direttamente
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* Indicatore Disponibilità */}
                        {checkingAvailability && (
                            <div className="loading-tables">
                                <p>🔍 Controllo disponibilità ristorante...</p>
                            </div>
                        )}

                        {/* Note Speciali */}
                        <div className="form-section">
                            <h2>Richieste Speciali (Opzionale)</h2>
                            <div className="form-group">
                                <label>
                                    <MessageSquare size={20} />
                                    Note
                                </label>
                                <textarea
                                    name="special_requests"
                                    value={formData.special_requests}
                                    onChange={handleInputChange}
                                    rows="4"
                                    placeholder="Allergie, intolleranze, occasioni speciali..."
                                />
                            </div>
                        </div>

                        <button 
                            type="submit" 
                            className="submit-btn"
                            disabled={loading || !capacityInfo?.available}
                        >
                            {loading ? 'Prenotazione in corso...' : 'Conferma Prenotazione'}
                        </button>
                    </form>

                    {/* Pulsante Cancella Prenotazione */}
                    <button 
                        onClick={() => setShowCancelModal(true)}
                        style={{
                            marginTop: '30px',
                            padding: '15px 30px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            width: '100%',
                            fontWeight: 'bold',
                            transition: 'background-color 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                    >
                        🗑️ Cancella una Prenotazione
                    </button>
                </div>
            </div>

            {/* Modale Cancellazione Prenotazione */}
            {showCancelModal && (
                <>
                    {/* Overlay */}
                    <div 
                        onClick={closeCancelModal}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(5px)',
                            zIndex: 999,
                            animation: 'fadeIn 0.3s ease'
                        }}
                    />

                    {/* Modale */}
                    <div 
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(255, 255, 255, 0.98)',
                            padding: '30px',
                            borderRadius: '15px',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                            zIndex: 1000,
                            maxWidth: '500px',
                            width: '90%',
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            border: '3px solid #d4af37',
                            animation: 'slideIn 0.3s ease'
                        }}
                    >
                        {/* Pulsante chiudi */}
                        <button
                            onClick={closeCancelModal}
                            style={{
                                position: 'absolute',
                                top: '15px',
                                right: '15px',
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '35px',
                                height: '35px',
                                fontSize: '20px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                        >
                            ×
                        </button>

                        <h2 style={{ marginBottom: '20px', color: '#1a1a1a', marginTop: 0 }}>
                            🗑️ Cancella Prenotazione
                        </h2>

                        {!cancelSuccess ? (
                            <>
                                <p style={{ marginBottom: '20px', color: '#666' }}>
                                    Inserisci il codice di prenotazione ricevuto via email
                                </p>

                                <div style={{ marginBottom: '20px' }}>
                                    <input
                                        type="text"
                                        placeholder="PREN-XXXXXX"
                                        value={cancelCode}
                                        onChange={(e) => setCancelCode(e.target.value.toUpperCase())}
                                        maxLength={12}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            fontSize: '16px',
                                            border: '2px solid #ddd',
                                            borderRadius: '8px',
                                            marginBottom: '10px',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                    <button
                                        onClick={handleSearchCancel}
                                        disabled={cancelLoading}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            backgroundColor: '#007bff',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: '16px',
                                            cursor: cancelLoading ? 'not-allowed' : 'pointer',
                                            fontWeight: 'bold',
                                            opacity: cancelLoading ? 0.6 : 1
                                        }}
                                    >
                                        {cancelLoading ? 'Ricerca...' : 'Cerca Prenotazione'}
                                    </button>
                                </div>

                                {cancelError && (
                                    <div style={{
                                        padding: '15px',
                                        backgroundColor: '#f8d7da',
                                        color: '#721c24',
                                        borderRadius: '8px',
                                        marginBottom: '15px',
                                        border: '1px solid #f5c6cb'
                                    }}>
                                        ⚠️ {cancelError}
                                    </div>
                                )}

                                {cancelBooking && (
                                    <div style={{
                                        border: '2px solid #d4af37',
                                        borderRadius: '10px',
                                        padding: '20px',
                                        backgroundColor: '#f8f9fa'
                                    }}>
                                        <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>Dettagli Prenotazione</h3>
                                        <div style={{ marginBottom: '10px', color: '#555' }}>
                                            <strong>Codice:</strong> {cancelBooking.booking_code}
                                        </div>
                                        <div style={{ marginBottom: '10px', color: '#555' }}>
                                            <strong>Nome:</strong> {cancelBooking.customer_name}
                                        </div>
                                        <div style={{ marginBottom: '10px', color: '#555' }}>
                                            <strong>Email:</strong> {cancelBooking.customer_email}
                                        </div>
                                        <div style={{ marginBottom: '10px', color: '#555' }}>
                                            <strong>Telefono:</strong> {cancelBooking.customer_phone}
                                        </div>
                                        <div style={{ marginBottom: '10px', color: '#555' }}>
                                            <strong>Data:</strong> {new Date(cancelBooking.booking_date).toLocaleDateString('it-IT', {
                                                weekday: 'long',
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </div>
                                        <div style={{ marginBottom: '10px', color: '#555' }}>
                                            <strong>Ora:</strong> {cancelBooking.booking_time.substring(0, 5)}
                                        </div>
                                        <div style={{ marginBottom: '20px', color: '#555' }}>
                                            <strong>Ospiti:</strong> {cancelBooking.number_of_guests}
                                        </div>

                                        <button
                                            onClick={handleCancelBooking}
                                            disabled={cancelLoading}
                                            style={{
                                                width: '100%',
                                                padding: '15px',
                                                backgroundColor: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                fontSize: '16px',
                                                cursor: cancelLoading ? 'not-allowed' : 'pointer',
                                                fontWeight: 'bold',
                                                opacity: cancelLoading ? 0.6 : 1
                                            }}
                                        >
                                            {cancelLoading ? 'Cancellazione...' : '🗑️ Conferma Cancellazione'}
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                <div style={{
                                    fontSize: '60px',
                                    color: '#28a745',
                                    marginBottom: '20px'
                                }}>
                                    ✓
                                </div>
                                <h3 style={{ marginBottom: '15px', color: '#333' }}>Prenotazione Cancellata</h3>
                                <p style={{ color: '#666', marginBottom: '20px' }}>
                                    La tua prenotazione è stata cancellata con successo.
                                </p>
                                <button
                                    onClick={closeCancelModal}
                                    style={{
                                        padding: '12px 30px',
                                        backgroundColor: '#d4af37',
                                        color: '#1a1a1a',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '16px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Chiudi
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Prenota;